import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	Alert,
	Box,
	CircularProgress,
	Paper,
	Typography,
} from "@mui/material";
import { useAuth } from "../../contexts/AuthContext";
import { useAcademicYear } from "../../contexts/AcademicYearContext";
import { ROLES } from "../../config/supabase";
import ServiceWindowGantt from "../../components/schools/ServiceWindowGantt";
import VisitTypeMessageModal from "../../components/schools/VisitTypeMessageModal";
import { getActiveVisitTypes } from "../../utils/serviceWindow";

const ServiceWindow = () => {
	const { supabase, user } = useAuth();
	const userProfile = user?.profile;
	const { currentAcademicYear } = useAcademicYear();
	const [visitTypes, setVisitTypes] = useState([]);
	const [messagesByTypeId, setMessagesByTypeId] = useState({});
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [selectedVisitType, setSelectedVisitType] = useState(null);
	const [modalOpen, setModalOpen] = useState(false);
	const [saving, setSaving] = useState(false);

	const isAdmin = userProfile?.role === ROLES.FUNMIYO_ADMIN;

	const fetchData = useCallback(async () => {
		try {
			setLoading(true);
			setError("");

			const [{ data: visitTypeData, error: visitTypeError }, { data: messageData, error: messageError }] =
				await Promise.all([
					supabase
						.from("visit_types")
						.select("*")
						.eq("status", true)
						.order("start_date", { ascending: true, nullsFirst: false })
						.order("name"),
					supabase
						.from("visit_type_messages")
						.select("*")
						.eq("is_active", true),
				]);

			if (visitTypeError) throw visitTypeError;
			if (messageError) throw messageError;

			setVisitTypes(visitTypeData || []);

			const messageMap = {};
			(messageData || []).forEach((message) => {
				messageMap[message.visit_type_id] = message;
			});
			setMessagesByTypeId(messageMap);
		} catch (err) {
			console.error("Error loading service window data:", err);
			setError("Failed to load service window data.");
		} finally {
			setLoading(false);
		}
	}, [supabase]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	const activeVisitTypes = useMemo(
		() => getActiveVisitTypes(visitTypes),
		[visitTypes]
	);

	const handleVisitTypeClick = (visitType) => {
		setSelectedVisitType(visitType);
		setModalOpen(true);
	};

	const handleSaveMessage = async (visitTypeId, messageHtml, existingMessageId) => {
		try {
			setSaving(true);
			setError("");

			await supabase
				.from("visit_type_messages")
				.update({ is_active: false, updated_at: new Date().toISOString() })
				.eq("visit_type_id", visitTypeId)
				.eq("is_active", true);

			if (existingMessageId) {
				const { error: updateError } = await supabase
					.from("visit_type_messages")
					.update({
						message_html: messageHtml,
						is_active: true,
						updated_at: new Date().toISOString(),
					})
					.eq("id", existingMessageId);

				if (updateError) throw updateError;
			} else {
				const { error: insertError } = await supabase.from("visit_type_messages").insert([
					{
						visit_type_id: visitTypeId,
						message_html: messageHtml,
						is_active: true,
						created_by: user?.id || null,
					},
				]);

				if (insertError) throw insertError;
			}

			await fetchData();
		} catch (err) {
			console.error("Error saving visit type message:", err);
			setError("Failed to save message template.");
		} finally {
			setSaving(false);
		}
	};

	if (loading) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
				<CircularProgress size={28} />
			</Box>
		);
	}

	return (
		<Box sx={{ p: { xs: 1, md: 2 } }}>
			<Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
				Service Window
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 900 }}>
				View the service visit schedule for {currentAcademicYear}. Click a visit type
				or its bar to open the current announcement template and copy it.
				{isAdmin
					? " Admins can also edit the active rich-text template for each visit type."
					: ""}
			</Typography>

			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			{activeVisitTypes.length > 0 && (
				<Paper
					elevation={0}
					sx={{
						p: 2,
						mb: 3,
						border: "1px solid",
						borderColor: "primary.light",
						bgcolor: "primary.50",
					}}>
					<Typography variant="subtitle2" color="primary.main" sx={{ mb: 1 }}>
						Currently active service windows
					</Typography>
					{activeVisitTypes.map((visitType) => (
						<Typography key={visitType.id} variant="body2" sx={{ mb: 0.5 }}>
							• {visitType.name}
						</Typography>
					))}
				</Paper>
			)}

			<ServiceWindowGantt
				visitTypes={visitTypes}
				academicYear={currentAcademicYear}
				onVisitTypeClick={handleVisitTypeClick}
			/>

			<VisitTypeMessageModal
				open={modalOpen}
				onClose={() => setModalOpen(false)}
				visitType={selectedVisitType}
				message={
					selectedVisitType ? messagesByTypeId[selectedVisitType.id] : null
				}
				isAdmin={isAdmin}
				onSave={handleSaveMessage}
				saving={saving}
			/>
		</Box>
	);
};

export default ServiceWindow;
